import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ModalWrapper from '@components/wrapper/Modal/ModalWrapper';
import Button from '@components/dataEntry/button/Button';
import { ModalInner } from '@components/wrapper/Modal/ModalStyle';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { useEditPermission, useMasterPermission } from '@hooks/usePermission';

import { useInputs } from '@hooks/useInputs';
import TextInput from '@components/dataEntry/Input/TextInput';
import SelectPhotocard from '@components/dataEntry/select/SelectPhotocard';
import { updateUserWallet } from '@store/firebase/user/updateUser';
import { getPathKey } from '@utils/getPath';
import { IstoreItem } from '@typings/store/storeItem.interface';
import {
  parchaseStickerItem,
  presentItem,
  parchaseItem,
  getItemData,
} from '@store/firebase/store/parchase-item';
import { CollectionPath } from '@typings/collections.enum';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import ParchaseConfirmModal from '@pages/Store/Detail/ParchaseConfirmModal';
import { fuego } from '@store/firebase';

import {
  ITEM_FUNCTION_TPYE,
  PET_SUB_FUNCTION_TYPE,
  STATUS_SUB_FUNCTION_TYPE,
} from '@typings/store/storeItemFunctionType';
import useInventoryModals from './useInventoryModals';
import inventoryActionMap from './inventoryActionMap';
import { ISkillItem } from '@typings/store/skill.interface';
import {
  IPhotoCard,
  IselectPhotocardList,
  MAX_INVENTORY_SLOTS,
  DEFAULT_INVENTORY_CONFIG,
} from '@typings/photocard/photocard.interface';
import { photocardAutoSetState } from '@atom/setting/photocardAutoSetState';
import ItemInfoSec from './ItemInfoSec';
import { representativePhotocardIDState } from '@atom/photoCard';
import { WalletHistoryType } from '@store/firebase/user/addWalletHistoryByType';
import { addItemHistory, ItemHistoryType } from '@store/firebase/store/addItemHistory';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import { IstoreCommon } from '@typings/store/storeCommon.interface';
import {
  DEFAULT_PHOTOCARD_EDIT_PERMISSION,
  IPhotocardEditPermission,
  PHOTOCARD_EDIT_PERMISSION_DOC,
} from '@typings/setting/photocardEditPermission.interface';
import {
  IstatusItem,
  IstatusSet,
} from '@typings/status/statusItem.interface';
import {
  resolveMaxStat,
  resolveCurrentStat,
} from '@utils/photocard/resolveMaxStat';

const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 6px 0;

  @media (max-width: 480px) {
    /* 수량 입력 + 주요 액션 버튼은 한 줄에 */
    & > input,
    & > button {
      flex-shrink: 0;
    }
  }
`;

const confirmMsgMap = {
  gift: {
    title: '선물 완료',
    contents: '상대 인벤토리를 확인하세요.',
  },
  reparchase: {
    title: '판매 완료',
    contents: '남은 소지금액을 확인하세요.',
  },
  use: {
    title: '아이템 사용 완료',
    contents: '캐릭터의 스탯 창에서 확인하세요.',
  },
  myroom: {
    title: '마이룸에 추가되었습니다!',
    contents: '마이룸에서 아이템을 확인하세요.',
  },
};

type Props = {
  data?: IstoreItem | ISkillItem | any;
  originData?: IstoreItem | ISkillItem | any;
  closeModal?: () => void;
  photocardData?: IPhotoCard;
};

function InventoryDetailModal({ data, closeModal, photocardData }: Props) {
  const [presentCharIDs, setSelectedOwnerId] = useState<IselectPhotocardList[]>(
    [],
  );
  const currentUid = useRecoilValue(currentUserIDState);
  const photocardAutoSet = useRecoilValue(photocardAutoSetState);
  const representativePhotocardID = useRecoilValue(
    representativePhotocardIDState,
  );
  const giftingCharID =
    getPathKey(2) || photocardData?.id || representativePhotocardID; // 포토카드 id로 '계정id와 다름'
  const [confirmMsgType, setConfirmMsgType] =
    useState<keyof typeof confirmMsgMap>('gift');

  const [openConfirm, setOpenConfirm] = useState(false);

  const closeConfirmModal = async () => {
    // 마이룸 추가 확인 후 닫을 때 인벤토리에서 아이템 삭제
    // (presentItem을 미리 호출하면 Firestore 리스너가 컴포넌트를 언마운트시켜 모달이 사라지는 문제 방지)
    if (confirmMsgType === 'myroom' && openConfirm) {
      try {
        await presentItem({
          collectionPath: CollectionPath.PHOTOCARD,
          giftingCharID,
          data,
        });
      } catch (error) {
        console.error('인벤토리 삭제 실패:', error);
      }
    }
    closeModal?.();
    setOpenConfirm(false);
  };
  const controlPermission = useEditPermission({
    authorUid: photocardData?.owner || '',
  }); // 해당 포토카드의 오너일때만 가능
  const masterPermission = useMasterPermission({});
  const [{ amount, giftMessage }, handleChange, reset] = useInputs({
    amount: 1,
    giftMessage: '',
  });
  const [disabledGiftBtn, setDisabledGiftBtn] = useState(true);
  const [showGift, setShowGift] = useState(false);
  // ── 중복 실행 방지 (스탯 2배 적용 버그 수정) ──
  const [isProcessing, setIsProcessing] = useState(false);
  const isProcessingRef = useRef(false);
  const currentUserID = useRecoilValue(currentUserIDState);

  // 포토카드 편집 권한 설정
  const { data: editPermissionData } = useDocument<IPhotocardEditPermission>(
    PHOTOCARD_EDIT_PERMISSION_DOC,
    { listen: false },
  );
  const resolvedPermission: IPhotocardEditPermission = {
    ...DEFAULT_PHOTOCARD_EDIT_PERMISSION,
    ...editPermissionData,
  };

  // 재판매 비율 설정 불러오기 (기본값 50%)
  const { data: storeSettingData } = useDocument<IstoreCommon>(
    `${CollectionPath.NPC}/storeSetting`,
    { listen: false },
  );
  const resellPercent = storeSettingData?.resellPercent ?? 50;

  // ── 스탯 최대값 폴백 체인용 레퍼런스 데이터 ──
  // photocardData.hp/mp 미설정 시 statusList.defaultMaxValue 또는 statusSetting.max 로 폴백한다.
  const { data: statusList } = useCollection<IstatusItem>(
    `${CollectionPath.STATUS}`,
    { listen: false },
  );
  const { data: statusSetting } = useDocument<IstatusSet>(
    `${CollectionPath.STATUS}/statusSetting`,
    { listen: false },
  );
  const isGiftReceived = data?.isPresent || !!data?.giftingCharID;
  const reParchasePrice = isGiftReceived
    ? 0
    : Math.round((Number(data?.price) * amount * resellPercent) / 100) || 0;

  // [선물] 버튼을 표시하는 경우
  // 1. 선물받은 아이템인지 확인 (giftingCharID가 있으면 선물받은 것)
  // 2. 랜덤 박스 아이템이 아닌 경우.
  const isGiftedItem =
    !!data?.giftingCharID ||
    data.functionType == ITEM_FUNCTION_TPYE.RANDOM_BOX ||
    data.functionType == ITEM_FUNCTION_TPYE.RANDOM_BOX_POST;

  // const isGiftedItem = !!data?.giftingCharID;

  const handleGift = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    try {
      await presentItem({
        collectionPath: CollectionPath.PHOTOCARD,
        giftingCharID,
        data: {
          ...data,
          title: data.title, // 아이템명
          giftMessage,
          giftingCharID,
        },
        presentCharID: presentCharIDs[0].key || '',
        sender: photocardData?.owner || currentUid || '',
      });
      if (currentUid) {
        addItemHistory({
          uid: currentUid,
          itemName: data.title || '',
          itemId: data.originItemID || data.id || '',
          type: ItemHistoryType.GIFT,
          count: -1,
          memo: `선물 → ${presentCharIDs[0]?.value || ''}`,
        }).catch(console.error);
      }
      setConfirmMsgType('gift');
      setOpenConfirm(true);
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  }, [giftingCharID, presentCharIDs, data, giftMessage, photocardData, currentUid]);

  const handleReParchase = useCallback(async () => {
    if (isProcessingRef.current) return;
    if (!confirm(`${data.title} 아이템을 정말 판매하시겠습니까?`)) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    try {
      if (!currentUid) return;
      // 지갑에 돈 추가
      await updateUserWallet({
        uids: [currentUid],
        price: reParchasePrice,
        type: WalletHistoryType.ITEM,
        memo: data.title || '',
      });

      // 인벤토리에서 삭제
      await presentItem({
        collectionPath: CollectionPath.PHOTOCARD,
        giftingCharID, //photocardID
        data,
        amount,
      });

      if (currentUid) {
        addItemHistory({
          uid: currentUid,
          itemName: data.title || '',
          itemId: data.originItemID || data.id || '',
          type: ItemHistoryType.SELL,
          count: -amount,
          memo: `판매 (+${reParchasePrice}G)`,
        }).catch(console.error);
      }
      setConfirmMsgType('reparchase');
      setOpenConfirm(true);
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  }, [reParchasePrice, currentUid, giftingCharID, data, amount]);

  const handleUseItem = useCallback(async () => {
    // ── 중복 실행 방지: ref 기반 가드 (listen:true race condition 대응) ──
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);

    try {
      if (!photocardData) {
        alert('캐릭터 정보를 찾을 수 없습니다.');
        console.error('❌ photocardData가 없습니다');
        return;
      }

      if (!giftingCharID) {
        alert('캐릭터 ID를 찾을 수 없습니다.');
        console.error('❌ giftingCharID가 없습니다');
        return;
      }

      // 인벤토리 아이템에서 기본 필드 읽기
      let statusId = data?.statusId;
      let addStatusValue = data?.addStatusValue;
      let statusSubFunctionType = data?.statusSubFunctionType;

      // STATUS 타입 아이템은 항상 원본 상점 데이터에서 최신 설정을 읽는다.
      // 인벤토리 스냅샷에 statusSubFunctionType이 없거나 구버전 데이터일 수 있으므로
      // 상점 원본을 source of truth 로 사용한다.
      const originItemId = data?.originItemID || data?.id;
      if (originItemId) {
        try {
          const originData = await getItemData({ itemID: originItemId });
          if (originData) {
            statusId = originData.statusId ?? statusId;
            addStatusValue = originData.addStatusValue ?? addStatusValue;
            // statusSubFunctionType 은 항상 상점 원본 값을 우선 사용
            statusSubFunctionType = originData.statusSubFunctionType ?? statusSubFunctionType;
          }
        } catch (error) {
          console.error('원본 상점 데이터 조회 실패:', error);
        }
      }

      // statusId 또는 addStatusValue가 여전히 없으면 경고
      if (!statusId) {
        alert(
          '아이템에 스탯 종류(statusId)가 설정되어 있지 않습니다. 상점에서 아이템 설정을 확인해주세요.',
        );
        console.error('❌ statusId가 없습니다:', data);
        return;
      }

      if (addStatusValue === undefined || addStatusValue === null) {
        alert(
          '아이템에 스탯 보정 수치(addStatusValue)가 설정되어 있지 않습니다. 상점에서 아이템 설정을 확인해주세요.',
        );
        console.error('❌ addStatusValue가 없습니다:', data);
        return;
      }

      // 필드명 및 최종값 결정
      const isHpOrMp = statusId === 'hp' || statusId === 'mp';
      const addValue = (Number(addStatusValue) || 0) * (Number(amount) || 1);

      // HP/MP인데 statusSubFunctionType이 누락된 경우 경고 로그(마이그레이션 감지용).
      // 기존 동작과의 하위호환을 위해 기본값은 add_max로 유지한다.
      if (isHpOrMp && !statusSubFunctionType) {
        console.warn(
          '[InventoryDetailModal] HP/MP 아이템에 statusSubFunctionType이 설정되지 않았습니다. add_max 로 간주합니다.',
          { itemId: originItemId, statusId },
        );
      }

      // HP/MP: statusSubFunctionType에 따라 업데이트 대상 필드 분기
      // - restore_current → 현재값 필드(currentHp/currentMp) 업데이트
      // - add_max 또는 미설정(기본값) → 최대값 필드(hp/mp) 업데이트 (피통/마통 증가)
      //   * UI의 getStatusLabel 도 미설정 시 "최대 체력(피통)"으로 표시하므로 동작을 일치시킴
      let updateField: string;
      let finalValue: number;

      if (isHpOrMp && statusSubFunctionType === STATUS_SUB_FUNCTION_TYPE.RESTORE_CURRENT) {
        // 현재값 회복: currentHp/currentMp 업데이트, 최대값 초과 불가
        updateField = statusId === 'hp' ? 'currentHp' : 'currentMp';

        // 최대값 폴백 체인: photocardData.hp → statusList.defaultMaxValue → statusSetting.max
        const maxValue = resolveMaxStat({
          photocardData,
          statusId,
          statusList,
          statusSetting,
        });

        if (maxValue <= 0) {
          // 최대값이 설정되어 있지 않으면 회복 결과가 0으로 clamp되는 버그를 방지하기 위해 즉시 중단.
          alert(
            `캐릭터의 최대 ${statusId === 'hp' ? 'HP' : 'MP'} 값이 설정되어 있지 않아 회복 아이템을 사용할 수 없습니다. 관리자에게 스탯 설정을 확인해 주세요.`,
          );
          console.error('❌ HP/MP 최대값 미설정으로 회복 차단:', {
            statusId,
            photocardData,
          });
          return;
        }

        const currentValue = resolveCurrentStat({
          photocardData,
          statusId,
          currentField: updateField,
        });
        finalValue = Math.min(Math.max(0, currentValue + addValue), maxValue);
      } else if (isHpOrMp) {
        // 피통/마통 증가: 최대값(hp/mp) 필드를 직접 올린다
        // add_max 또는 미설정(undefined/null) 모두 여기에 해당
        updateField = statusId; // 'hp' 또는 'mp'
        // 동일 폴백 체인을 사용하여 "0에서 시작해 증가분만 반영"되는 버그를 방지한다.
        const prevMax = resolveMaxStat({
          photocardData,
          statusId,
          statusList,
          statusSetting,
        });
        finalValue = Math.max(0, prevMax + addValue);
      } else {
        // 기타 전투 스탯 또는 커스텀 스탯
        const battleStats = ['attack', 'defense', 'critical', 'speed'];
        const isBattleStat = battleStats.includes(statusId);
        updateField = isBattleStat
          ? `current${statusId.charAt(0).toUpperCase() + statusId.slice(1)}`
          : statusId;
        // current{Stat}이 Finite number이면 그 값을, 아니면 base 스탯으로 폴백.
        // (기존 `?? 0` 체인은 current{Stat}=0 잔존 시 base를 건너뛰어 수치가 0으로 리셋되는 버그가 있었음)
        const prevValue = resolveCurrentStat({
          photocardData,
          statusId,
          currentField: updateField,
        });
        finalValue = Math.max(0, prevValue + addValue);
      }

      // 직접 Firestore 업데이트 (정확한 필드만 업데이트)
      await fuego.db
        .collection(CollectionPath.PHOTOCARD)
        .doc(giftingCharID)
        .update({
          [updateField]: finalValue,
          editedAt: new Date(),
        });

      console.log('✅ 스탯 업데이트 완료:', {
        [updateField]: finalValue,
      });

      // 인벤토리에서 아이템 삭제
      await presentItem({
        collectionPath: CollectionPath.PHOTOCARD,
        giftingCharID, //photocardID
        data,
        amount,
      });

      if (currentUid) {
        addItemHistory({
          uid: currentUid,
          itemName: data.title || '',
          itemId: data.originItemID || data.id || '',
          type: ItemHistoryType.USE_STATUS,
          count: -amount,
          memo: `${updateField} ${addValue >= 0 ? '+' : ''}${addValue}`,
        }).catch(console.error);
      }

      setConfirmMsgType('use');
      setOpenConfirm(true);
    } catch (error) {
      console.error('❌ 스탯 업데이트 실패:', error);
      alert('아이템 사용 중 오류가 발생했습니다.');
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  }, [giftingCharID, data, photocardData, amount, currentUid, statusList, statusSetting]);

  const handleAddMyroom = useCallback(async () => {
    if (data.functionType === ITEM_FUNCTION_TPYE.MYROOM) {
      try {
        await parchaseStickerItem({
          data,
          photocardID: giftingCharID,
          buyerID: currentUserID || '',
        });

        if (currentUserID) {
          addItemHistory({
            uid: currentUserID,
            itemName: data.title || '',
            itemId: data.originItemID || data.id || '',
            type: ItemHistoryType.USE_MYROOM,
            count: -1,
            memo: '마이룸에 추가',
          }).catch(console.error);
        }

        // 모달을 먼저 띄운 뒤 인벤토리 삭제는 모달 닫을 때 수행
        // (presentItem이 Firestore 리스너를 자극해 컴포넌트를 언마운트시키는 문제 방지)
        setConfirmMsgType('myroom');
        setOpenConfirm(true);
      } catch (error) {
        console.error('마이룸 추가 실패:', error);
        alert('마이룸 추가 중 오류가 발생했습니다.');
      }
    }
  }, [currentUserID, giftingCharID, data]);

  const handleDeleteItem = useCallback(async () => {
    // 관리자 권한일때만 표시되는 아이템 삭제 버튼
    // 인벤토리에서 삭제
    if (
      confirm(`${data.title} 아이템이 인벤토리에서 1개 삭제됩니다. (관리자용)`)
    ) {
      await presentItem({
        collectionPath: CollectionPath.PHOTOCARD,
        giftingCharID, //photocardID
        data,
      });
      if (currentUid) {
        addItemHistory({
          uid: currentUid,
          itemName: data.title || '',
          itemId: data.originItemID || data.id || '',
          type: ItemHistoryType.DELETE,
          count: -1,
          memo: '관리자 삭제',
        }).catch(console.error);
      }
    } else return;
  }, [data, giftingCharID, currentUid]);

  const handleUseRandomBox = useCallback(async () => {
    if (isProcessingRef.current) return;
    if (!data?.randomBoxData) {
      alert('랜덤박스 정보가 없습니다.');
      return;
    }
    isProcessingRef.current = true;
    setIsProcessing(true);

    const { itemList = [], skillList = [] } = data.randomBoxData;
    const allItems = [...itemList, ...skillList];

    if (allItems.length === 0) {
      alert('뽑을 수 있는 아이템이 없습니다.');
      isProcessingRef.current = false;
      setIsProcessing(false);
      return;
    }

    // 랜덤으로 아이템 선택
    const randomIndex = Math.floor(Math.random() * allItems.length);
    const pickItem = allItems[randomIndex];

    try {
      // 선택된 아이템 정보 가져오기
      const itemData = await getItemData({ itemID: pickItem });

      if (!itemData) {
        alert('아이템 정보를 가져올 수 없습니다.');
        return;
      }

      // 랜덤박스를 인벤토리에서 제거
      await presentItem({
        collectionPath: CollectionPath.PHOTOCARD,
        giftingCharID, //photocardID
        data,
        amount: 1,
      });

      // 뽑은 아이템을 인벤토리에 추가
      await parchaseItem({
        collectionPath: CollectionPath.PHOTOCARD,
        photocardID: giftingCharID,
        data: { ...itemData, price: 0, id: pickItem },
        amount: 1,
        buyerID: currentUid || '',
      });

      if (currentUid) {
        addItemHistory({
          uid: currentUid,
          itemName: data.title || '',
          itemId: data.originItemID || data.id || '',
          type: ItemHistoryType.USE_RANDOM_BOX,
          count: -1,
          memo: `획득: ${itemData.title || ''}`,
        }).catch(console.error);
      }

      alert(`🎉 ${itemData.title}을(를) 획득했습니다!`);
      closeModal?.();
    } catch (error) {
      console.error('랜덤박스 오픈 실패:', error);
      alert('랜덤박스 오픈에 실패했습니다.');
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  }, [data, giftingCharID, currentUid, closeModal]);

  /**
   * 가방 확장권 사용: inventoryConfig.expandedRows += expandRows
   * 양수: 최대 행(100) 상한 / 음수: 최소 1행 하한 적용
   */
  const handleUseBagExpand = useCallback(async () => {
    if (isProcessingRef.current) return;
    const expandRows = Number(data?.bagExpandData?.expandRows);
    if (!expandRows || expandRows === 0) {
      alert('확장권에 설정된 행 수가 없습니다. 관리자에게 문의하세요.');
      return;
    }

    if (!giftingCharID) {
      alert('캐릭터 ID를 찾을 수 없습니다.');
      return;
    }
    isProcessingRef.current = true;
    setIsProcessing(true);

    try {
      const globalConfig = (photocardAutoSet as any)?.inventoryConfig ?? DEFAULT_INVENTORY_CONFIG;
      const gridRows = globalConfig.gridRows ?? 8;

      const closetCollectionPath = `${getPathKey(1)}Closet`;
      const closetDocRef = fuego.db.collection(closetCollectionPath).doc(giftingCharID);
      const closetSnap = await closetDocRef.get();
      const closetData = closetSnap.data();
      const currentExpandedRows = closetData?.inventoryConfig?.expandedRows ?? 0;

      const MAX_ROWS = 100;
      let safeExpandRows: number;

      if (expandRows > 0) {
        // 확장: 상한 초과 방지
        const maxAddable = MAX_ROWS - gridRows - currentExpandedRows;
        if (maxAddable <= 0) {
          alert(`가방이 이미 최대 행 수(${MAX_ROWS}행)에 도달했습니다.`);
          return;
        }
        safeExpandRows = Math.min(expandRows, maxAddable);
      } else {
        // 축소: 유저 확장분(currentExpandedRows)까지만 축소 가능, 관리자 기본 행(gridRows) 이하로는 불가
        if (currentExpandedRows <= 0) {
          alert('확장된 행이 없어 더 이상 축소할 수 없습니다.');
          return;
        }
        safeExpandRows = Math.max(expandRows, -currentExpandedRows);

        // 축소될 행에 아이템이 장착되어 있으면 차단
        // gridColumns는 뷰포트 폭 기준 추정 (GRID_ITEM_SIZE=50, BagFrame padding=8)
        const GRID_ITEM_SIZE = 50;
        const estimatedColumns = Math.max(
          1,
          Math.floor((window.innerWidth - 8) / GRID_ITEM_SIZE),
        );
        const newTotalRows = gridRows + currentExpandedRows + safeExpandRows;
        const newTotalSlots = newTotalRows * estimatedColumns;

        const inventoryCollectionPath = `${CollectionPath.PHOTOCARD}/${giftingCharID}/inventory`;
        const inventorySnap = await fuego.db
          .collection(inventoryCollectionPath)
          .where('slotIndex', '>=', newTotalSlots)
          .get();
        const occupiedInRemovedArea = inventorySnap.docs.length > 0;
        if (occupiedInRemovedArea) {
          alert(
            `축소될 행에 아이템이 장착되어 있습니다.\n해당 아이템을 미정리로 이동한 후 다시 시도해주세요.`,
          );
          return;
        }
      }

      await closetDocRef.set(
        {
          inventoryConfig: {
            expandedRows: currentExpandedRows + safeExpandRows,
          },
        },
        { merge: true },
      );

      // 인벤토리에서 확장권 아이템 제거
      await presentItem({
        collectionPath: CollectionPath.PHOTOCARD,
        giftingCharID,
        data,
        amount: 1,
      });

      const isReduce = safeExpandRows < 0;

      if (currentUid) {
        addItemHistory({
          uid: currentUid,
          itemName: data.title || '',
          itemId: data.originItemID || data.id || '',
          type: ItemHistoryType.USE_BAG_EXPAND,
          count: -1,
          memo: `가방 ${Math.abs(safeExpandRows)}행 ${isReduce ? '축소' : '확장'}`,
        }).catch(console.error);
      }

      alert(`🎒 가방이 ${Math.abs(safeExpandRows)}행 ${isReduce ? '축소' : '확장'}되었습니다!`);
      closeModal?.();
    } catch (error) {
      console.error('가방 확장/축소 실패:', error);
      alert('아이템 사용 중 오류가 발생했습니다.');
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  }, [data, giftingCharID, closeModal, photocardAutoSet, currentUid]);

  // 사용하기 버튼에 따른 모달 출력
  const { renderModalPortal, openers } = useInventoryModals({
    data,
    currentUid,
    photocardData,
    ownerID: photocardData?.owner || '',
    amount,
  });

  useEffect(() => {
    setDisabledGiftBtn(presentCharIDs.length === 0);
  }, [presentCharIDs]);

  const actionBtn = inventoryActionMap(
    data?.functionType,
    data?.petFunction?.subFunctionType,
    {
      onUseStatus: () => handleUseItem(),
      onAddMyroom: () => handleAddMyroom(),
      onOpenBettingDemo: () => openers.openBettingDemo(),
      onOpenQuestCreate: () => openers.openQuestCreate(),
      onOpenUserGift: () => openers.openUserGift(),
      onOpenChangeName: () => openers.openChangeName(),
      onOpenChangeAppearance: () => openers.openChangeAppearance(),
      onOpenChangeBackground: () => openers.openChangeBackground(),
      onUseRandomBox: () => handleUseRandomBox(),
      onUseBagExpand: () => handleUseBagExpand(),
    },
    { photocardID: giftingCharID, itemId: data?.id, data, currentUid },
    isProcessing,
  );

  if (!data) return null;

  return (
    <>
      <ModalWrapper
        outsideClick={closeConfirmModal}
        contentStyle={{ minWidth: 'min(500px, calc(100vw - 32px))' }}
      >
        <>
          <ModalInner>
            <CloseBtn fixed onClick={closeModal} size="md" />

            <ItemInfoSec data={data} />

            {controlPermission && (
              <>
                <ButtonWrap>
                  <>
                    <TextInput
                      value={amount}
                      name="amount"
                      onChange={handleChange}
                      type="number"
                      placeholder="1"
                      max={data.multiple?.length}
                      min={0}
                      style={{ width: '80px' }}
                    />
                    <Button onClick={handleReParchase} shape="stroke" disabled={isProcessing}>
                      💳 판매 ({reParchasePrice}G)
                    </Button>
                    {/* 선물받은 아이템이 아니고, 선물하기 권한이 활성화된 경우에만 표시 */}
                    {!isGiftedItem && resolvedPermission.giftItem && (
                      <Button onClick={() => setShowGift((prev) => !prev)} shape="stroke">
                        🎁 선물
                      </Button>
                    )}
                  </>
                  {/* 사용하기 버튼 */}
                  {actionBtn}
                  {/* 관리자 권한일때만 표시되는 아이템 삭제 버튼 */}
                  {masterPermission && (
                    <Button onClick={handleDeleteItem} color="warning">
                      아이템 삭제(관리자)
                    </Button>
                  )}
                </ButtonWrap>

                {showGift && (
                  <>
                    <ButtonWrap
                      style={{
                        marginTop: 8,
                        borderTop: '1px solid var(--lineColor)',
                        padding: '12px 0',
                      }}
                    >
                      <SelectPhotocard
                        initialList={presentCharIDs}
                        setUserList={setSelectedOwnerId}
                        onlySelect
                        goDirect={false}
                        defaultValue="캐릭터 선택"
                        block
                      />
                      <TextInput
                        value={giftMessage}
                        name="giftMessage"
                        onChange={handleChange}
                        placeholder="선물 메시지"
                      />
                      <Button
                        onClick={handleGift}
                        shape="stroke"
                        disabled={disabledGiftBtn || isProcessing}
                      >
                        🎁 선물하기
                      </Button>
                    </ButtonWrap>

                  </>
                )}
              </>
            )}
          </ModalInner>
        </>
      </ModalWrapper>

      {/* 구매 완료 모달 */}
      {openConfirm && (
        <ParchaseConfirmModal
          closeModal={closeConfirmModal}
          data={data}
          title={confirmMsgMap[confirmMsgType].title}
          contents={confirmMsgMap[confirmMsgType].contents}
        />
      )}

      {/* 사용하기 버튼에 따른 모달 출력 */}
      {renderModalPortal()}
    </>
  );
}
export default InventoryDetailModal;
